<template>
  <Dialog
    width="860px"
    :height="createRenderType == 'mobile' ? '100%' : '720px'"
    titleAlign="left"
    :title="$$t('editor.region_editing')"
    v-model:visible="renderData.visible"
    @close="closeDialog"
    :background="
      createRenderType == 'mobile' && !props.item?.song_id
        ? 'linear-gradient(180deg, rgba(255, 189, 141, 0.3) 0%, rgba(255, 189, 141, 0) 100%), black'
        : null
    "
    mobileRenderType="drawerDialog"
    :customClass="`region-editing-dialog ${
      createRenderType == 'mobile' ? 'region-editing-dialog--mobile' : ''
    }`"
    :questionClick="createRenderType == 'mobile' ? onQuestionClick : null"
  >
    <template v-slot:subtitle>
      <div class="region-editing-dialog-subtitle">
        <template
          v-for="(item, index) in $$t('editor.region_editing_subtitle').split(
            '\n'
          )"
          :key="index"
        >
          <p class="region-editing-dialog-subtitle-text">
            {{ item }}
          </p>
        </template>

        <p
          class="region-editing-dialog-subtitle-text"
          v-if="!renderData.hideRemove"
        >
          {{
            $$tValue("editor.region_editing_edit_description_credit", {
              song_count: 1,
              credit_count: 2,
            })
          }}
        </p>
      </div>
    </template>
    <div
      class="region-editing-dialog-container"
      :class="{
        [`region-editing-dialog-container--${createRenderType}`]: true,
      }"
    >
      <div class="region-editing-dialog-edit-box">
        <div class="region-editing-dialog-media-box">
          <div
            class="region-editing-dialog-media-upload"
            v-if="renderData.editMode == 'upload'"
          >
            <AudioDragUpload
              pointTypePrefix="region_editing"
              :uploadDescription="$$t('editor.region_editing_edit_description')"
              @initEditData="doInitEditData"
              @syncUploadState="doSyncUploadState"
            ></AudioDragUpload>
          </div>
          <div
            class="region-editing-dialog-media-processing"
            v-if="renderData.editMode == 'processing'"
          >
            <div class="redm-processing-loading-box">
              <div class="redm-processing-loading rotate-loading"></div>
              <div class="redm-processing-loading-ico flex-center">
                <i class="icon"> </i>
              </div>
            </div>
            <div class="region-editing-dialog-media-processing-text">
              {{ $$t("editor.audio_edit_data_processing") }}
            </div>
          </div>
          <div
            class="region-editing-dialog-media-processfailed"
            v-if="renderData.editMode == 'processfailed'"
          >
            <div class="region-processing-processfailed-ico"></div>
            <div class="region-editing-dialog-media-processfailed-text tc">
              {{ $$t("editor.get_editing_data_failed") }}
            </div>
            <div class="region-editing-dialog-media-processfailed-button">
              <div
                class="region-editing-dialog-media-processfailed-button-item"
                v-if="!props.item?.song_id"
              >
                <Button
                  @click="onCancelClick"
                  :renderStyle="{
                    '--padding': '0',
                    '--width': '100%',
                    '--height': '32px',
                    '--borderRadius': '48px',
                    '--background': 'rgba(255, 255, 255, 0.2)',
                    '--fontColor': '#fff',
                    '--fontSize': '14px',
                    '--fontWeight': 'normal',
                    '--border': 'none',
                    '--backgroundHover': 'rgba(255, 255, 255, 0.3)',
                    '--fontColorHover': '#fff',
                  }"
                >
                  <span class="fn-inline">{{ $$t("create.cancel") }}</span>
                </Button>
              </div>
              <div
                class="region-editing-dialog-media-processfailed-button-item"
              >
                <Button
                  @click="onRetryClick"
                  :renderStyle="{
                    '--padding': '0',
                    '--width': '100%',
                    '--height': '32px',
                    '--borderRadius': '48px',
                    '--background': 'rgba(255, 255, 255, 0.2)',
                    '--fontColor': '#fff',
                    '--fontSize': '14px',
                    '--fontWeight': 'normal',
                    '--border': 'none',
                    '--backgroundHover': 'rgba(255, 255, 255, 0.3)',
                    '--fontColorHover': '#fff',
                  }"
                >
                  <span class="fn-inline">{{ $$t("create.retry") }}</span>
                </Button>
              </div>
            </div>
          </div>

          <div
            class="region-editing-dialog-media-edit"
            v-if="renderData.editMode == 'editing'"
          >
            <EditSelectAudio
              type="select"
              :url="renderData.audioUrl"
              :duration="renderData.audioDuration"
              :waveformData="renderData.waveformData"
              :hideRemove="renderData.hideRemove"
              @syncSelectData="doSyncSelectData"
              @remove="doRemove"
              @selectChangeLimit="doSelectChangeLimit"
            ></EditSelectAudio>
          </div>
        </div>
        <div class="region-editing-dialog-lyrics-box">
          <ToggleEditLyrics
            ref="refToggleEditLyrics"
            type="regionEditing"
            :placeholder="$$t('editor.region_editing_lyric_placeholder')"
            :disabled="renderData.editMode != 'editing'"
            :loading="renderData.editMode == 'processing'"
            :maxLength="renderData.lyricMaxLength"
            :lyric="renderData.lyric"
            @change="onChangeLyric"
            :getBeforeLyrics="getBeforeLyrics"
          ></ToggleEditLyrics>
        </div>
      </div>
      <div class="region-editing-dialog-button-box">
        <CreditsButton
          :buttonText="$$t('editor.edit_region')"
          :disabled="disabledSubmit"
          :loading="renderData.loadingSubmit"
          @onClick="onSubmitClick"
          :membership_source="renderData.membership_source"
        ></CreditsButton>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  getCurrentInstance,
} from "vue"

import { useRouter, useRoute } from "vue-router"
import globalConfig, { chinaAppName } from "@/config/config.js"

import apiEditor from "@/api/api/apiEditor.js"
import apiCommon from "@/api/api/apiCommon.js"

import { showActionDialog } from "@/components/functionCallComponent/action/action.js"

import AudioDragUpload from "../component/audioDragUpload.vue"
import ToggleEditLyrics from "../component/toggleEditLyrics.vue"
import CreditsButton from "../component/creditsButton.vue"
import AddLinkDialog from "../component/addLinkDialog.vue"
import Button from "@/components/basic/button/Button.vue"
import EditSelectAudio from "../component/editSelectAudio.vue"

import tools from "@/utils/tools.js"
import ffmpegUtil from "@/components/media/utils/ffmpegUtil.js"
import message from "@/components/functionCallComponent/message.jsx"
import clipUtil from "@/components/media/utils/clipUtil.js"
import objectStorage, {
  getObjectStorageFullUrl,
} from "@/lib/objectStorage/objectStorage.js"

import { storeToRefs } from "pinia"
import useCreateLayoutStore from "@/store/create/createLayoutStore.js"
import useUserDataStore from "@/store/userDataStore.js"
import useFeedListStore from "@/store/create/feedListDataStore.js"

const createLayoutStore = useCreateLayoutStore()
const userDataStore = useUserDataStore()
const { createRenderType } = storeToRefs(createLayoutStore)
const feedListDataStore = useFeedListStore()
const { $$language, $$t, $$tm, $$tValue } = getCurrentInstance().appContext?.config.globalProperties.$i18n ?? {}

const Router = useRouter()
const route = useRoute()

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties

const props = defineProps({
  pitem: {
    type: Object,
    default: null,
  },
  item: {
    type: Object,
    default: null,
  },
  visible: {
    type: Boolean,
    default: false,
  },
})

const refToggleEditLyrics = ref(null)
const emits = defineEmits(["update:visible"])

const stateData = {
  parseDataParams: null,
  lyricUnitTimeDataArray: [],
}

const renderData = reactive({
  visible: props.visible,
  editMode: "upload", // "upload", "processing","editing", "processfailed"
  addLinkDialogVisible: false,
  hideRemove: props.item?.song_id ? true : false,
  linkParseUrl: "",
  file: null,
  linkUrl: "",
  clipSourceData: null,
  editFileName: "",
  audioUrl: "", // https://test.mureka.ai/cos-test/song/audio/20250213/music_56098979577857_PfJV1K6VMYcGNNWnzYropf_bcfiec.mp3",
  audioDuration: 160.183,
  waveformData: Array.from(
    {
      length: 1000,
    },
    (v, i) => Math.random()
  ),
  lyric: "",
  lyricMaxLength: 100,
  loadingSubmit: false,
  membership_source: {
    membership_source1: props.item?.song_id ? "membership_feature" : "editor",
    membership_source2: props.item?.song_id ? "regenerate" : "region_editing",
  },
})

const disabledSubmit = computed(() => {
  return renderData.editMode !== "editing"
})

const onChangeLyric = (data) => {
  renderData.lyric = data
}

const doDownloadFile = async (url, filename) => {
  return new Promise((resolve, reject) => {
    var xhr = new XMLHttpRequest()
    xhr.open("GET", url, true)
    xhr.responseType = "blob"
    xhr.onload = function (e) {
      if (this.status === 200) {
        var blob = new Blob([this.response], {
          type: this.response?.type || "text/plain",
        })
        const resfile = new File([blob], filename, { type: blob.type })
        resolve(resfile)
      } else {
        reject(this.statusText)
      }
    }
    xhr.send()
  })
}

const closeDialog = () => {
  if (renderData.editMode === "editing") {
    showActionDialog({
      content: $$t("editor.edit_close_warning"),
      cancelLabel: $$t("editor.keep"),
      confirmLabel: $$t("create.close"),
      titleAlign: "center",
      confirm: (closeAction) => {
        closeAction()
        renderData.visible = false
      },
    })
  } else {
    renderData.visible = false
  }
}

const onQuestionClick = () => {
  showActionDialog({
    customClass: "question-help-dialog",
    title: $$t("editor.note_on_using_region_edit"),
    content:
      $$t("editor.region_editing_subtitle") +
      (renderData.hideRemove
        ? ""
        : "\n" +
          $$tValue("editor.region_editing_edit_description_credit", {
            song_count: 1,
            credit_count: 2,
          })),
    hideCancel: true,
    confirmLabel: "ok",
    titleAlign: "left",
    confirm: (closeAction) => {
      closeAction()
    },
  })
}

const doSyncSelectData = async (data) => {
  renderData.from = data.from
  renderData.to = data.to
  renderData.duration = data.duration

  updateSetLyricText(renderData.from * 1000, renderData.to * 1000)
}

const doSelectChangeLimit = (data) => {
  if (["min", "max"].includes(data)) {
    message.info({
      content: $$tValue("editor.region_editing_select_limit_warning", {
        duration: 12,
      }),
    })
  }
}

const doRemove = async () => {
  showActionDialog({
    content: $$t("editor.edit_remove_warning"),
    cancelLabel: $$t("editor.keep"),
    confirmLabel: $$t("create.delete"),
    titleAlign: "center",
    confirm: (closeAction) => {
      closeAction()
      renderData.editMode = "upload"
      renderData.lyric = ""
      renderData.lyricSource = ""
      renderData.from = 0
      renderData.to = 0
      renderData.lyricMaxLength = 100
      stateData.lyricUnitTimeDataArray = []
    },
  })
}

const doSyncUploadState = () => {}

const updateSetLyricText = (from, to) => {
  let text = ""
  stateData.lyricUnitTimeDataArray.forEach((item, index) => {
    if (
      (item.start <= from && from < item.end) ||
      (from <= item.start && item.end <= to) ||
      (item.start < to && to <= item.end)
    ) {
      if (!(item.start < to && to <= item.end && item.type != "tag")) {
        text += item.text
        text += item.space
        text += item.break
      }
    }
  })
  renderData.lyric = text

  console.log(
    "🍎🍎🍎🍎🍎🍎updateSetLyricText",
    from,
    to,
    text,
    stateData.lyricUnitTimeDataArray
  )
  renderData.lyricMaxLength = Math.max(
    text.length + Math.ceil(text.length * 0.15),
    0
  )
}

const getBeforeLyrics = () => {
  const from = renderData.from * 1000
  let text = ""
  stateData.lyricUnitTimeDataArray.forEach((item, index) => {
    if (
      item.end < from ||
      (item.start <= from && from < item.end && item.type == "tag")
    ) {
      text += item.text
      text += item.space
      text += item.break
    }
  })
  return text
}

const doInitEditData = async (data) => {
  renderData.editMode = "processing"
  doParseInitEditData(data)
}

const generateLyricUnitTimeDataArray = (lyrics) => {
  return lyrics.reduce((res, item) => {
    if (item.user_input_tag) {
      res.push({
        start: item.start,
        end: item.start + 3000,
        endSource: item.end,
        text: item.user_input_tag,
        type: "tag",
        space: "",
        break: "\n",
      })
    }
    if (item.rows) {
      res.push(
        ...item.rows.reduce((resR, row) => {
          if (row.words) {
            let text = row.text
            resR.push(
              ...row.words.reduce((resRW, word, windex) => {
                let wordD = {
                  start: word.start,
                  end: word.end,
                  text: word.text ?? "",
                  space: "",
                  type: "word",
                  break: windex == row.words.length - 1 ? "\n" : "",
                }
                resRW.push(wordD)
                text = text.slice((word.text ?? "").length)
                while (text.startsWith(" ")) {
                  text = text.slice(1)
                  wordD.space += " "
                }
                return resRW
              }, [])
            )
          }
          return resR
        }, [])
      )
    }

    return res
  }, [])
}

const doParseInitEditData = (data) => {
  renderData.editMode = "processing"
  stateData.parseDataParams = {
    resource: data.linkUrl,
    source_type: data.source_type,
    cos_url: data?.cos_url,
    duration_milliseconds: data?.duration_milliseconds,
    title: data?.title,
    is_need_waves: true,
    is_need_lyrics: true,
    check_mute_duration: true,
  }
  return new Promise((resolve, reject) => {
    apiEditor
      .parseAudioData(stateData.parseDataParams)
      .then((res) => {
        if (res.code == 200) {
          renderData.waveformData = res.data.waves
          renderData.audioDuration = res.data.duration_milliseconds / 1000
          renderData.audioUrl =
            getObjectStorageFullUrl(res.data.mp3_url) + "?type=extendEditing"
          renderData.title = res.data.title
          renderData.editMode = "editing"
          stateData.lyricUnitTimeDataArray = generateLyricUnitTimeDataArray(
            res.data.lyrics ?? []
          )
          stateData.parseResData = res.data
        } else {
          if (res.code == 6346) {
            // message.error({
            //   content: $$t("editor.check_mute_duration_warning"),
            //   positon: "top",
            // })
            message.error({
              content: $$tValue("editor.vocals_min_duration", {
                duration: 5
              }),
            })
            onCancelClick()
          } else if (res.code == 6316) {
            message.error({
              content: $$t("create.song_state_auditing_failed"),
              positon: "top",
            })
            onCancelClick()
          } else {
            message.error({
              content: $$t("editor.get_editing_data_failed_message"),
              positon: "top",
            })
            renderData.editMode = "processfailed"
          }
        }
      })
      .catch((err) => {
        message.error({
          content: $$t("editor.get_editing_data_failed_message"),
          positon: "top",
        })
        renderData.editMode = "processfailed"
        reject(err)
      })
  })
}

const doParseInitEditDataWithSong = () => {
  renderData.editMode = "processing"
  return new Promise((resolve, reject) => {
    apiCommon
      .getSongInfo({ song_id: props.item.song_id, check_mute_duration: true })
      .then((res) => {
        if (res.code == 200) {
          renderData.waveformData = res.data.waves
          renderData.audioDuration = res.data.duration_milliseconds / 1000
          renderData.audioUrl =
            getObjectStorageFullUrl(res.data.mp3_url) + "?type=extendEditing"
          renderData.title = res.data.title
          renderData.editMode = "editing"
          stateData.lyricUnitTimeDataArray = generateLyricUnitTimeDataArray(
            res.data.lyrics
          )
          stateData.parseResData = res.data
        } else {
          if (res.code == 6346) {
            // message.error({
            //   content: $$t("editor.check_mute_duration_warning"),
            //   positon: "top",
            // })
            message.error({
              content: $$tValue("editor.vocals_min_duration", {
                duration: 5
              }),
            })
            renderData.editMode = "processfailed"
          } else if (res.code == 6316) {
            message.error({
              content: $$t("create.song_state_auditing_failed"),
              positon: "top",
            })
           renderData.editMode = "processfailed"
          } else {
            message.error({
              content: $$t("editor.get_editing_data_failed_message"),
              positon: "top",
            })
            renderData.editMode = "processfailed"
          }
        }
      })
      .catch((err) => {
        renderData.editMode = "processfailed"
        reject(err)
      })
  })
}

const onRetryClick = () => {
  if (props.item) {
    doParseInitEditDataWithSong()
  } else {
    doParseInitEditData(stateData.parseDataParams)
  }
}
const onCancelClick = () => {
  renderData.editMode = "upload"
  renderData.lyric = ""
  renderData.lyricSource = ""
  renderData.from = 0
  renderData.to = 0
  renderData.lyricMaxLength = 100
  stateData.lyricUnitTimeDataArray = []
}

const onSubmitClick = () => {
  globalProperties.logCustomEvent("region_editing_button_click", {
    tab_stare: "region_editing",
    content_id: props.item?.song_id,
    source: props.item?.song_id
      ? "mysong"
      : stateData.parseDataParams?.source_type == 1
      ? "youtube_link_upload"
      : "file_upload",
  })
  renderData.loadingSubmit = true
  feedListDataStore
    .doReginEditSongDoneReGenerate(
      {
        parent_song_id: props.item?.song_id,
        type: 1,
        start_milliseconds: Math.floor(renderData.from * 1000),
        end_milliseconds: Math.floor(renderData.to * 1000),
        lyrics: refToggleEditLyrics.value?.getLyrics(),
        title: renderData.title,
        generation_method: 3,
        is_generated_from_uploaded_file: !props.item?.song_id,
        outer_song: props.item?.song_id
          ? null
          : {
              id: stateData.parseResData?.id,
            },
      },
      renderData.membership_source
    )
    .then(() => {
      renderData.loadingSubmit = false
      renderData.visible = false
    })
    .catch((err) => {
      renderData.loadingSubmit = false
    })
}

const init = async () => {
  if (props.item) {
    doParseInitEditDataWithSong()
  }
}

const poninterEvent = () => {
  if (renderData.visible) {
    if (props.item) {
      globalProperties.logCustomEvent("create_audio_edit_tab_stare_show", {
        tab_stare: "region_editing",
        content_id: props.item?.song_id,
      })
    } else {
      globalProperties.logCustomEvent("create_audio_edit_tab_stare_show", {
        tab_stare: "region_editing",
      })
    }
  }
}
watch(
  () => {
    return props.visible
  },
  () => {
    renderData.visible = props.visible
    poninterEvent()
  }
)
watch(
  () => {
    return renderData.visible
  },
  () => {
    emits("update:visible", renderData.visible)
  }
)

onMounted(() => {
  init()
  poninterEvent()
})
</script>
<style lang="scss">
.region-editing-dialog {
  .region-editing-dialog-subtitle {
    font-family: HarmonyOS Sans SC;
    font-size: 14px;
    font-weight: normal;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.5);
    margin: 3px 0 0 0;
    .region-editing-dialog-subtitle-text {
      padding: 0 0 0 15px;
      position: relative;
      &:before {
        content: "";
        display: inline-block;
        width: 2px;
        height: 2px;
        background: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        position: absolute;
        left: 5px;
        top: 9px;
      }
    }
  }
  &.region-editing-dialog--mobile {
    .dialog-subtitle {
      display: none;
    }
  }
}
.region-editing-dialog-container {
  padding: 13px 40px 40px 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
  .region-editing-dialog-edit-box {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 0;
    .region-editing-dialog-media-box {
      border-radius: 16px 16px 0 0;
      background: rgba(148, 173, 255, 0.2);
    }
  }

  .region-editing-dialog-media-upload {
  }
  .region-editing-dialog-media-processing {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 196px;
    padding: 20px 0 16px 0;
    .redm-processing-loading-box {
      width: 100px;
      height: 100px;
      position: relative;

      .redm-processing-loading {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: conic-gradient(
          from 180deg at 50% 50%,
          rgba(148, 173, 255, 0.28) 0deg,
          rgba(148, 173, 255, 0.8) 54deg,
          rgba(148, 173, 255, 0.2) 95deg,
          rgba(148, 173, 255, 0.1) 136deg,
          rgba(148, 173, 255, 0.28) 178deg,
          rgba(148, 173, 255, 0.8) 236deg,
          rgba(148, 173, 255, 0.2) 279deg,
          rgba(148, 173, 255, 0.1) 321deg,
          rgba(148, 173, 255, 0.28) 360deg,
          rgba(148, 173, 255, 0.8) 414deg
        );
        animation: animation-rotate-infinite 0.75s infinite normal linear;
      }
      .redm-processing-loading-ico {
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        i {
          height: 100%;
          width: 100%;
          background: url(@/assets/img/editor/upload-drag-loading-ico.png);
          background-size: 100% 100%;
        }
      }
    }
    .region-editing-dialog-media-processing-text {
      font-family: HarmonyOS Sans SC;
      font-size: 14px;
      font-weight: normal;
      line-height: normal;
      color: #ffffff;
      margin: 16px 0 0 0;
    }
  }
  .region-editing-dialog-media-processfailed {
    padding: 19px 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .region-processing-processfailed-ico {
      height: 80px;
      width: 80px;
      background: url(@/assets/img/editor/upload-processing-failed-ico.png);
      background-size: 100% 100%;
    }
    .region-editing-dialog-media-processfailed-text {
      font-family: HarmonyOS Sans SC;
      font-size: 16px;
      font-weight: normal;
      line-height: 22px;
      color: #ffffff;
      margin: 12px 12px;
    }
    .region-editing-dialog-media-processfailed-button {
      display: flex;
      justify-content: center;
      align-items: center;
      .region-editing-dialog-media-processfailed-button-item {
        width: 140px;
        &:last-child {
          margin: 0 0 0 10px;
        }
      }
    }
  }
  .region-editing-dialog-media-edit {
    height: 162px;
  }
  .region-editing-dialog-lyrics-box {
    margin: 1px 0 0 0;
    height: 0;
    flex: 1;
  }

  .region-editing-dialog-button-box {
    margin: 20px 0 0 0;
  }
  &.region-editing-dialog-container--mobile {
    padding: 13px 0 16px 0;

    .region-editing-dialog-edit-box {
      overflow-y: auto;
      padding: 0 16px;
    }
    .region-editing-dialog-media-processing {
      height: 160px;
      padding: 22px 0 16px 0;
      .redm-processing-loading-box {
        width: 80px;
        height: 80px;
      }
      .region-editing-dialog-media-processing-text {
        font-size: 14px;
        line-height: 16px;
        margin: 12px 0 0 0;
      }
    }
    .region-editing-dialog-media-processfailed {
      padding: 19px 0;
      .region-processing-processfailed-ico {
        height: 64px;
        width: 64px;
      }
      .region-editing-dialog-media-processfailed-text {
        font-size: 14px;
        line-height: 16px;
        margin: 12px 12px;
      }
    }
    .region-editing-dialog-media-edit {
      height: 160px;
    }
    .region-editing-dialog-button-box {
      margin: 16px 16px 0 16px;
    }
  }
}
</style>
